import { useCallback, useState, type ReactNode } from "react";
import type { Notification, UnreadSummary } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import {
  NOTIFICATION_KIND_LABEL,
  SEVERITY_MARK,
  SEVERITY_TONE,
  SEVERITY_WORD,
  closedBecause,
} from "../lib/comms.js";
import { utcMoment } from "../lib/format.js";

/**
 * The notification hub — one feed, one unread count.
 *
 * Architecture decision 9: every producer publishes here, and staff read one
 * list. The alternative this replaces is three badges that each look like
 * somebody else's problem.
 *
 * Two things the screen has to be careful about.
 *
 * Severity is not a volume knob. "Breach" is a compliance window that closed
 * unsatisfied, where the remedy is a recorded non-conformity; "urgent" is a
 * reminder. They get different words and different shapes, not different
 * shades, because these boards get printed in black and white for site
 * meetings and some of the people reading them cannot tell amber from red.
 *
 * Marking read is per person and, once done, cannot be undone from here — the
 * first timestamp wins, because "when did they see it" has one true answer. The
 * bulk control therefore says exactly how far it reaches, which is further than
 * the list in front of you when a filter is on.
 */

interface Feed {
  readonly notifications: readonly Notification[];
  readonly badge: UnreadSummary;
}

export function Notifications(): ReactNode {
  const { client } = usePortal();
  const [liveOnly, setLiveOnly] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const { state, reload } = useAsync<Feed>(
    async () => ({
      notifications: await client.notifications({
        ...(liveOnly ? { liveOnly: true } : {}),
        ...(unreadOnly ? { unreadOnly: true } : {}),
      }),
      badge: await client.notificationUnread(),
    }),
    [client, liveOnly, unreadOnly],
  );

  return (
    <>
      <div className="page-header spread">
        <div>
          <h1>Notifications</h1>
          <p className="lede">
            One feed for every alert this platform raises — compliance clocks,
            urgent and overdue tasks, signatures, document deliveries and
            messages. Nothing here is raised by a person: every producer is a
            service, so a badge cannot be forged by opening a screen.
          </p>
        </div>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <fieldset className="scope">
        <legend>What to show</legend>
        <label>
          <input
            type="checkbox"
            checked={liveOnly}
            onChange={(event) => setLiveOnly(event.target.checked)}
          />{" "}
          Only what still needs acting on
        </label>{" "}
        <label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(event) => setUnreadOnly(event.target.checked)}
          />{" "}
          Only what I have not read
        </label>
      </fieldset>

      <Async state={state} what="your notifications" reload={reload}>
        {(feed) => (
          <FeedView
            feed={feed}
            filtered={liveOnly || unreadOnly}
            reload={reload}
          />
        )}
      </Async>
    </>
  );
}

function FeedView({
  feed,
  filtered,
  reload,
}: {
  readonly feed: Feed;
  readonly filtered: boolean;
  readonly reload: () => void;
}): ReactNode {
  return (
    <>
      <Badge badge={feed.badge} />
      <MarkEverything
        badge={feed.badge}
        filtered={filtered}
        showing={feed.notifications.length}
        reload={reload}
      />

      {feed.notifications.length === 0 ? (
        <p className="empty">
          {filtered
            ? "Nothing matches the filter. Clear it to see the rest of the feed."
            : "Nothing has been raised for you. An empty feed means no service has published anything addressed to you, which is not the same as nothing being wrong on the jobs themselves — the compliance board is where a clock is read."}
        </p>
      ) : (
        <ul className="stack">
          {feed.notifications.map((notification) => (
            <Item
              key={notification.id}
              notification={notification}
              reload={reload}
            />
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * The badge, in words.
 *
 * One count and the worst severity in it, because two numbers side by side
 * invite somebody to read the smaller one. `highestSeverity` comes from the API
 * rather than being worked out from the list, so a filtered view cannot make
 * the badge disagree with the hub.
 */
function Badge({ badge }: { readonly badge: UnreadSummary }): ReactNode {
  if (badge.total === 0 || badge.highestSeverity === null) {
    return (
      <p className="lede" role="status" aria-live="polite">
        Nothing unread.
      </p>
    );
  }
  return (
    <p className="lede" role="status" aria-live="polite">
      <span className={`status-word status-${SEVERITY_TONE[badge.highestSeverity]}`}>
        <span aria-hidden="true">{SEVERITY_MARK[badge.highestSeverity]} </span>
        {SEVERITY_WORD[badge.highestSeverity]}
      </span>{" "}
      {badge.total} unread, the worst of them{" "}
      {SEVERITY_WORD[badge.highestSeverity].toLowerCase()}.
    </p>
  );
}

/**
 * Clearing the badge, with the reach of the control said out loud.
 *
 * `POST /api/notifications/read-all` marks every notification this person
 * holds, not the ones on screen — the API takes no filter — so a button
 * labelled "mark these read" under a filtered list would be lying about what it
 * does. There is no route that marks a subset, and rather than approximate one
 * by looping (which would half-succeed and leave nobody able to say where it
 * stopped), the screen says what the API can actually do.
 */
function MarkEverything({
  badge,
  filtered,
  showing,
  reload,
}: {
  readonly badge: UnreadSummary;
  readonly filtered: boolean;
  readonly showing: number;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [confirming, setConfirming] = useState(false);

  if (badge.total === 0) return null;

  if (!confirming) {
    return (
      <p className="row-actions">
        <button className="secondary" onClick={() => setConfirming(true)}>
          Mark everything read
        </button>
      </p>
    );
  }

  return (
    <div className="notice warn" role="alert">
      <p>
        <strong>
          This marks all {badge.total} unread notifications read, not the{" "}
          {showing} on this screen.
        </strong>{" "}
        {filtered
          ? "A filter is on, so the feed in front of you is a subset. The hub takes no filter on this call — there is no way to mark only what is shown."
          : "The hub takes no filter on this call."}
      </p>
      <p>
        Read state is per person and the first timestamp wins, so this cannot be
        undone from here.
      </p>
      {error ? <Failure error={error} what="the notifications" /> : null}
      <p className="row-actions">
        <button
          disabled={busy}
          onClick={() =>
            void submit(async () => {
              await client.markAllNotificationsRead();
              setConfirming(false);
              reload();
            })
          }
        >
          Mark all {badge.total} read
        </button>
        <button className="secondary" onClick={() => setConfirming(false)}>
          Leave them
        </button>
      </p>
    </div>
  );
}

function Item({
  notification,
  reload,
}: {
  readonly notification: Notification;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const closed = closedBecause(notification);

  const markRead = useCallback(
    () =>
      void submit(async () => {
        await client.markNotificationRead(notification.id);
        reload();
      }),
    [client, notification.id, reload, submit],
  );

  return (
    <li className="row">
      <article className={notification.readAt === null ? "room" : "room muted"}>
        <p>
          <span
            className={`status-word status-${SEVERITY_TONE[notification.severity]}`}
          >
            <span aria-hidden="true">{SEVERITY_MARK[notification.severity]} </span>
            {SEVERITY_WORD[notification.severity]}
          </span>{" "}
          <span className="standard small">
            {NOTIFICATION_KIND_LABEL[notification.kind]}
          </span>
        </p>

        <h3>{notification.title}</h3>
        {notification.body === null ? null : <p>{notification.body}</p>}

        <dl className="fields small">
          <div>
            <dt>Raised</dt>
            <dd className="ts">{utcMoment(notification.createdAt)}</dd>
          </div>
          <div>
            <dt>Still needs acting on</dt>
            <dd>
              {notification.live ? (
                "Yes"
              ) : (
                <>
                  No. <span className="muted">{closed}</span>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Read</dt>
            <dd>
              {notification.readAt === null ? (
                <span className="status-word status-approaching">
                  <span aria-hidden="true">◔ </span>
                  Not read
                </span>
              ) : (
                <>Read {utcMoment(notification.readAt)}</>
              )}
            </dd>
          </div>
          {notification.escalationLevel === null ? null : (
            <div>
              <dt>Rung on the escalation ladder</dt>
              <dd>{notification.escalationLevel}</dd>
            </div>
          )}
        </dl>

        {notification.jobId === null ? (
          <p className="muted small">
            No job is named on this notification, so there is nothing to open
            from here.
          </p>
        ) : (
          <p className="row-actions">
            <a href={href.job(notification.jobId)}>Open the job</a>
          </p>
        )}

        {error ? <Failure error={error} what="this notification" /> : null}

        {notification.readAt === null ? (
          <p className="row-actions">
            <button className="link" disabled={busy} onClick={markRead}>
              Mark this one read
            </button>
          </p>
        ) : null}
      </article>
    </li>
  );
}
